import { useCallback, useEffect, useState } from 'react';

type ScrollTarget = { scrollIntoView: (options: ScrollIntoViewOptions) => void };

export function scrollAfterNavigation(
  hash: string,
  findTarget: (id: string) => ScrollTarget | null,
  scrollToTop: () => void,
) {
  if (hash) {
    const target = findTarget(hash.slice(1));
    if (!target) return false;
    target.scrollIntoView({ behavior: 'smooth' });
    return true;
  }

  scrollToTop();
  return true;
}

export function scrollWhenTargetIsMounted(
  hash: string,
  findTarget: (id: string) => ScrollTarget | null,
  observeForTarget: (onChange: () => void) => { disconnect: () => void },
) {
  if (!hash || scrollAfterNavigation(hash, findTarget, () => undefined)) return () => undefined;

  const observer = observeForTarget(() => {
    if (scrollAfterNavigation(hash, findTarget, () => undefined)) observer.disconnect();
  });
  return () => observer.disconnect();
}

export function useRoute() {
  const [route, setRoute] = useState(
    () => `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  const location = new URL(route, window.location.origin);
  const pathname = location.pathname;

  useEffect(() => {
    const onPopState = () =>
      setRoute(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const hash = new URL(route, window.location.origin).hash;
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    return scrollWhenTargetIsMounted(
      hash,
      (id) => document.getElementById(id),
      (onChange) => {
        const observer = new MutationObserver(onChange);
        observer.observe(document.body, { childList: true, subtree: true });
        return observer;
      },
    );
  }, [route]);

  const navigate = useCallback((path: string, replace = false) => {
    const destination = new URL(path, window.location.origin);
    window.history[replace ? 'replaceState' : 'pushState'](
      {},
      '',
      `${destination.pathname}${destination.search}${destination.hash}`,
    );
    setRoute(`${destination.pathname}${destination.search}${destination.hash}`);
  }, []);

  return { pathname, navigate };
}
